import { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { useNavigate } from "react-router-dom";
import PaymentService from "../../services/PaymentService";
import QuickPaymentDialog from "../../components/Finance/QuickPaymentDialog";

const currencyFormatter = new Intl.NumberFormat("tr-TR", {
  style: "currency",
  currency: "TRY",
  maximumFractionDigits: 2,
});
const formatMoney = (val) => currencyFormatter.format(Number(val) || 0);

const sourceTypeOptions = [
  { label: "Tüm Kaynaklar", value: "all" },
  { label: "Tedarikçi Borçları", value: "supplier" },
  { label: "Kredi Kartı Taksitleri", value: "cc_installment" },
  { label: "Firma Çekleri", value: "cheque" },
  { label: "Firma Senetleri", value: "promissory_note" },
  { label: "İşletme Giderleri", value: "expense" },
];

export default function PaymentsPage() {
  const [mounted, setMounted] = useState(false);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({
    overdueAmount: 0,
    overdueCount: 0,
    todayAmount: 0,
    todayCount: 0,
    thisWeekAmount: 0,
    thisWeekCount: 0,
    thisMonthAmount: 0,
    thisMonthCount: 0,
    totalPendingAmount: 0,
    totalPendingCount: 0,
    totalLiquidBalance: 0,
  });
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState("");
  const [period, setPeriod] = useState("all"); // all, overdue, today, week, month, custom
  const [sourceType, setSourceType] = useState("all");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Quick Payment Modal State
  const [isPaymentDialogVisible, setIsPaymentDialogVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const toast = useRef(null);
  const dt = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    fetchPayments();
  }, [period, sourceType, startDate, endDate]);

  const formatDateParam = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const params = {
        period: period,
        sourceType: sourceType,
        startDate: period === "custom" ? formatDateParam(startDate) : null,
        endDate: period === "custom" ? formatDateParam(endDate) : null,
      };

      const res = await PaymentService.getUpcomingPayments(params);
      if (res && res.isSuccess && res.data) {
        setPayments(res.data.items || []);
        if (res.data.summary) {
          setSummary(res.data.summary);
        }
      } else if (res && res.data) {
        setPayments(res.data.items || []);
        if (res.data.summary) {
          setSummary(res.data.summary);
        }
      }
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Ödemeler yüklenirken hata oluştu.",
      });
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setPeriod("all");
    setSourceType("all");
    setStartDate(null);
    setEndDate(null);
    setGlobalFilter("");
  };

  const exportExcel = () => {
    if (dt.current) {
      dt.current.exportCSV({ selectionOnly: false });
    }
  };

  // Open Payment Dialog
  const openPaymentModal = (item) => {
    setSelectedPayment(item);
    setIsPaymentDialogVisible(true);
  };

  // Badge helpers
  const getDaysRemainingBadge = (rowData) => {
    const days = rowData.daysRemaining;
    if (days < 0) {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-black text-rose-700 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40 px-2 py-0.5 rounded-full border border-rose-200 dark:border-rose-900/60 shadow-sm animate-pulse">
          <i className="pi pi-exclamation-triangle text-[10px]" />
          {Math.abs(days)} gün gecikti
        </span>
      );
    }
    if (days === 0) {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-black text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 px-2 py-0.5 rounded-full border border-amber-200 dark:border-amber-900/60 shadow-sm">
          <i className="pi pi-clock text-[10px]" />
          Bugün
        </span>
      );
    }
    if (days === 1) {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-black text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-950/40 px-2 py-0.5 rounded-full border border-orange-200 dark:border-orange-900/60">
          <i className="pi pi-arrow-right text-[10px]" />
          Yarın
        </span>
      );
    }
    if (days <= 7) {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-black text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/40 px-2 py-0.5 rounded-full border border-blue-200 dark:border-blue-900/60">
          <i className="pi pi-calendar text-[10px]" />
          {days} gün kaldı
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full border border-slate-200 dark:border-slate-700">
        <i className="pi pi-calendar text-[10px]" />
        {days} gün sonra
      </span>
    );
  };

  const getSourceTypeBadge = (sourceType) => {
    switch (sourceType) {
      case "SUPPLIER_DUE":
      case "PURCHASE":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-black bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 border border-indigo-200/80 dark:border-indigo-800 whitespace-nowrap shrink-0">
            <i className="pi pi-truck text-xs" />
            <span>Tedarikçi Faturası</span>
          </span>
        );
      case "CC_INSTALLMENT":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-black bg-purple-50 dark:bg-purple-950/50 text-purple-700 dark:text-purple-300 border border-purple-200/80 dark:border-purple-800 whitespace-nowrap shrink-0">
            <i className="pi pi-credit-card text-xs" />
            <span>Kredi Kartı Taksiti</span>
          </span>
        );
      case "CHEQUE":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-black bg-teal-50 dark:bg-teal-950/50 text-teal-700 dark:text-teal-300 border border-teal-200/80 dark:border-teal-800 whitespace-nowrap shrink-0">
            <i className="pi pi-id-card text-xs" />
            <span>Firma Çeki</span>
          </span>
        );
      case "PROMISSORY_NOTE":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-black bg-cyan-50 dark:bg-cyan-950/50 text-cyan-700 dark:text-cyan-300 border border-cyan-200/80 dark:border-cyan-800 whitespace-nowrap shrink-0">
            <i className="pi pi-file text-xs" />
            <span>Firma Senedi</span>
          </span>
        );
      case "EXPENSE":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-black bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 border border-amber-200/80 dark:border-amber-800 whitespace-nowrap shrink-0">
            <i className="pi pi-wallet text-xs" />
            <span>İşletme Gideri</span>
          </span>
        );
      default:
        return (
          <Tag value={sourceType} severity="info" className="text-[10px] font-bold shrink-0" />
        );
    }
  };

  const periodButtons = [
    { label: "Tümü", value: "all", count: summary.totalPendingCount, icon: "pi-list" },
    { label: "Vadesi Geçmiş", value: "overdue", count: summary.overdueCount, icon: "pi-exclamation-triangle" },
    { label: "Bugün", value: "today", count: summary.todayCount, icon: "pi-clock" },
    { label: "Bu Hafta", value: "week", count: summary.thisWeekCount, icon: "pi-calendar-times" },
    { label: "Bu Ay", value: "month", count: summary.thisMonthCount, icon: "pi-calendar" },
    { label: "Özel Tarih", value: "custom", icon: "pi-sliders-h" },
  ];

  const header = (
    <div className="space-y-6">
      {/* Top Header Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div
            className="bg-rose-600 rounded-xl shadow-lg shadow-rose-600/20 text-white flex items-center justify-center flex-shrink-0"
            style={{
              width: "48px",
              height: "48px",
              minWidth: "48px",
              maxWidth: "48px",
              minHeight: "48px",
              maxHeight: "48px",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className="pi pi-calendar text-white text-xl" style={{ fontSize: "1.25rem" }}></i>
          </div>
          <div>
            <h1 className="text-xl font-black text-slate-800 dark:text-white m-0">
              Ödeme Takvimi & Nakit Çıkış Planı
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-0.5">
              Vadesi geçen, bugün, bu hafta ve bu ay ödenecek tüm borç ve giderler
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="p-input-icon-left w-full md:w-72">
            <i className="pi pi-search" />
            <InputText
              type="search"
              value={globalFilter}
              onInput={(e) => setGlobalFilter(e.target.value)}
              placeholder="Kime, belge no veya açıklama ara..."
              className="w-full rounded-xl border-slate-200 dark:border-slate-700 py-2.5 text-xs font-bold"
            />
          </span>

          <Button
            icon="pi pi-file-excel"
            label="Dışa Aktar"
            className="p-button-outlined p-button-secondary rounded-xl text-xs font-black py-2.5"
            onClick={exportExcel}
            tooltip="CSV / Excel Olarak İndir"
          />

          <Button
            icon="pi pi-refresh"
            className="p-button-text p-button-secondary rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 py-2.5"
            onClick={fetchPayments}
            loading={loading}
            tooltip="Yenile"
          />
        </div>
      </div>

      {/* Period Filter Pill Bar & Source Type Dropdown */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 pt-2 border-t border-slate-100 dark:border-slate-800 p-2 bg-slate-50 dark:bg-slate-800/60 rounded-2xl border border-slate-200/80 dark:border-slate-700/80">
        {/* Period Buttons Group */}
        <div className="flex flex-wrap items-center gap-1.5 p-1 bg-slate-200/60 dark:bg-slate-900/80 rounded-xl border border-slate-300/50 dark:border-slate-800">
          {periodButtons.map((btn) => {
            const isActive = period === btn.value;

            let btnClasses = "inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-bold transition-all duration-150 cursor-pointer select-none border-none outline-none ";
            if (isActive) {
              switch (btn.value) {
                case 'overdue':
                  btnClasses += "!bg-rose-600 text-white shadow-sm font-black";
                  break;
                case 'today':
                  btnClasses += "!bg-amber-500 text-white shadow-sm font-black";
                  break;
                case 'week':
                  btnClasses += "!bg-cyan-600 text-white shadow-sm font-black";
                  break;
                case 'month':
                  btnClasses += "!bg-indigo-600 text-white shadow-sm font-black";
                  break;
                case 'custom':
                  btnClasses += "!bg-emerald-600 text-white shadow-sm font-black";
                  break;
                case 'all':
                default:
                  btnClasses += "!bg-blue-600 text-white shadow-sm font-black";
                  break;
              }
            } else {
              btnClasses += "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-white/60 dark:hover:bg-slate-800/60";
            }

            let badgeClasses = "text-[11px] px-2 py-0.5 rounded-full font-black leading-none transition-all ";
            if (isActive) {
              badgeClasses += "bg-white/20 text-white";
            } else {
              if (btn.value === 'overdue' && btn.count > 0) {
                badgeClasses += "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300 border border-rose-300 dark:border-rose-800 animate-pulse";
              } else if (btn.value === 'today' && btn.count > 0) {
                badgeClasses += "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 border border-amber-300 dark:border-amber-800";
              } else if (btn.value === 'week' && btn.count > 0) {
                badgeClasses += "bg-cyan-100 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-300 border border-cyan-300 dark:border-cyan-800";
              } else if (btn.value === 'month' && btn.count > 0) {
                badgeClasses += "bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300 border border-indigo-300 dark:border-indigo-800";
              } else {
                badgeClasses += "bg-slate-300/80 text-slate-700 dark:bg-slate-700 dark:text-slate-200";
              }
            }

            return (
              <button
                key={btn.value}
                type="button"
                style={{ border: 'none', outline: 'none' }}
                onClick={() => setPeriod(btn.value)}
                className={btnClasses}
              >
                <i className={`pi ${btn.icon} text-xs`} />
                <span>{btn.label}</span>
                {btn.count !== undefined && (
                  <span className={badgeClasses}>
                    {btn.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Source Type Dropdown & Reset */}
        <div className="flex items-center gap-2">
          <Dropdown
            value={sourceType}
            options={sourceTypeOptions}
            onChange={(e) => setSourceType(e.value)}
            placeholder="Tüm Kaynaklar"
            appendTo={typeof document !== "undefined" ? document.body : "self"}
            className="rounded-xl border-slate-200 dark:border-slate-700 text-xs font-bold w-48 h-[38px] flex items-center bg-white dark:bg-slate-800 shadow-sm"
          />

          {(period !== "all" || sourceType !== "all" || startDate || endDate || globalFilter) && (
            <Button
              icon="pi pi-filter-slash"
              label="Temizle"
              outlined
              severity="secondary"
              className="!rounded-xl !text-xs !font-black !py-2 !h-[38px]"
              onClick={clearFilters}
              tooltip="Tüm Filtreleri Temizle"
            />
          )}
        </div>
      </div>

      {/* Custom Date Range Picker (Shown when 'Özel Tarih' is selected) */}
      {period === "custom" && (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 bg-slate-50 dark:bg-slate-900/60 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 animate-fade-in items-end">
          <div className="md:col-span-4 flex flex-col gap-1">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">
              Başlangıç Vade Tarihi
            </label>
            <Calendar
              value={startDate}
              onChange={(e) => setStartDate(e.value)}
              showIcon
              dateFormat="dd.mm.yy"
              placeholder="Seçiniz..."
              appendTo={typeof document !== "undefined" ? document.body : "self"}
              className="w-full"
              inputClassName="rounded-xl border-slate-200 dark:border-slate-700 py-2 font-bold text-xs bg-white dark:bg-slate-800"
            />
          </div>
          <div className="md:col-span-4 flex flex-col gap-1">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">
              Bitiş Vade Tarihi
            </label>
            <Calendar
              value={endDate}
              onChange={(e) => setEndDate(e.value)}
              showIcon
              dateFormat="dd.mm.yy"
              placeholder="Seçiniz..."
              appendTo={typeof document !== "undefined" ? document.body : "self"}
              className="w-full"
              inputClassName="rounded-xl border-slate-200 dark:border-slate-700 py-2 font-bold text-xs bg-white dark:bg-slate-800"
            />
          </div>
          <div className="md:col-span-4 flex gap-2">
            <Button
              label="Tarihe Göre Getir"
              icon="pi pi-search"
              className="w-full !bg-rose-600 border-none hover:!bg-rose-700 !rounded-xl !font-black !text-xs !py-2.5 shadow-md shadow-rose-600/20"
              onClick={fetchPayments}
            />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-3 md:p-6 space-y-6 animate-fade-in">
      <Toast ref={toast} appendTo={mounted ? document.body : "self"} />

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Overdue Card */}
        <div
          onClick={() => setPeriod("overdue")}
          className={`cursor-pointer transition-all rounded-3xl p-5 border relative overflow-hidden flex flex-col justify-between ${
            period === "overdue"
              ? "bg-rose-500 text-white shadow-xl shadow-rose-500/25 border-rose-600 ring-2 ring-rose-400"
              : "bg-white dark:bg-slate-800/90 text-slate-800 dark:text-white border-slate-200 dark:border-slate-700/80 hover:shadow-lg hover:border-rose-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className={`text-[11px] font-black uppercase tracking-wider ${period === "overdue" ? "text-rose-100" : "text-rose-600 dark:text-rose-400"}`}>
              🔴 Vadesi Geçmiş
            </span>
            <span className={`text-xs font-black px-2 py-0.5 rounded-full ${period === "overdue" ? "bg-white/20 text-white" : "bg-rose-100 dark:bg-rose-950 text-rose-700 dark:text-rose-300"}`}>
              {summary.overdueCount} Kalem
            </span>
          </div>
          <div className="mt-4">
            <div className="text-2xl font-black tracking-tight">
              {formatMoney(summary.overdueAmount)}
            </div>
            <p className={`text-[10px] font-semibold mt-1 m-0 ${period === "overdue" ? "text-rose-100" : "text-slate-400"}`}>
              Hemen ödenmesi gereken borçlar
            </p>
          </div>
        </div>

        {/* Today Card */}
        <div
          onClick={() => setPeriod("today")}
          className={`cursor-pointer transition-all rounded-3xl p-5 border relative overflow-hidden flex flex-col justify-between ${
            period === "today"
              ? "bg-amber-500 text-white shadow-xl shadow-amber-500/25 border-amber-600 ring-2 ring-amber-400"
              : "bg-white dark:bg-slate-800/90 text-slate-800 dark:text-white border-slate-200 dark:border-slate-700/80 hover:shadow-lg hover:border-amber-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className={`text-[11px] font-black uppercase tracking-wider ${period === "today" ? "text-amber-100" : "text-amber-600 dark:text-amber-400"}`}>
              🟠 Bugün Ödenecek
            </span>
            <span className={`text-xs font-black px-2 py-0.5 rounded-full ${period === "today" ? "bg-white/20 text-white" : "bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300"}`}>
              {summary.todayCount} Kalem
            </span>
          </div>
          <div className="mt-4">
            <div className="text-2xl font-black tracking-tight">
              {formatMoney(summary.todayAmount)}
            </div>
            <p className={`text-[10px] font-semibold mt-1 m-0 ${period === "today" ? "text-amber-100" : "text-slate-400"}`}>
              Bugün çıkacak toplam nakit
            </p>
          </div>
        </div>

        {/* This Week Card */}
        <div
          onClick={() => setPeriod("week")}
          className={`cursor-pointer transition-all rounded-3xl p-5 border relative overflow-hidden flex flex-col justify-between ${
            period === "week"
              ? "bg-blue-600 text-white shadow-xl shadow-blue-600/25 border-blue-700 ring-2 ring-blue-400"
              : "bg-white dark:bg-slate-800/90 text-slate-800 dark:text-white border-slate-200 dark:border-slate-700/80 hover:shadow-lg hover:border-blue-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className={`text-[11px] font-black uppercase tracking-wider ${period === "week" ? "text-blue-100" : "text-blue-600 dark:text-blue-400"}`}>
              🟡 Bu Hafta (7 Gün)
            </span>
            <span className={`text-xs font-black px-2 py-0.5 rounded-full ${period === "week" ? "bg-white/20 text-white" : "bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300"}`}>
              {summary.thisWeekCount} Kalem
            </span>
          </div>
          <div className="mt-4">
            <div className="text-2xl font-black tracking-tight">
              {formatMoney(summary.thisWeekAmount)}
            </div>
            <p className={`text-[10px] font-semibold mt-1 m-0 ${period === "week" ? "text-blue-100" : "text-slate-400"}`}>
              Haftalık kısa vadeli yük
            </p>
          </div>
        </div>

        {/* This Month Card */}
        <div
          onClick={() => setPeriod("month")}
          className={`cursor-pointer transition-all rounded-3xl p-5 border relative overflow-hidden flex flex-col justify-between ${
            period === "month"
              ? "bg-indigo-600 text-white shadow-xl shadow-indigo-600/25 border-indigo-700 ring-2 ring-indigo-400"
              : "bg-white dark:bg-slate-800/90 text-slate-800 dark:text-white border-slate-200 dark:border-slate-700/80 hover:shadow-lg hover:border-indigo-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className={`text-[11px] font-black uppercase tracking-wider ${period === "month" ? "text-indigo-100" : "text-indigo-600 dark:text-indigo-400"}`}>
              🔵 Bu Ay Toplam
            </span>
            <span className={`text-xs font-black px-2 py-0.5 rounded-full ${period === "month" ? "bg-white/20 text-white" : "bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300"}`}>
              {summary.thisMonthCount} Kalem
            </span>
          </div>
          <div className="mt-4">
            <div className="text-2xl font-black tracking-tight">
              {formatMoney(summary.thisMonthAmount)}
            </div>
            <p className={`text-[10px] font-semibold mt-1 m-0 ${period === "month" ? "text-indigo-100" : "text-slate-400"}`}>
              Ay sonuna kadar bekleyenler
            </p>
          </div>
        </div>

        {/* Total Liquid Cash & Bank Card */}
        <div className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white rounded-3xl p-5 shadow-xl shadow-emerald-600/20 border border-emerald-500 flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-black uppercase tracking-wider text-emerald-100">
              🟢 Kasa & Banka Likit
            </span>
            <i className="pi pi-wallet text-white text-base" />
          </div>
          <div className="mt-4">
            <div className="text-2xl font-black tracking-tight">
              {formatMoney(summary.totalLiquidBalance)}
            </div>
            <p className="text-[10px] font-medium mt-1 m-0 text-emerald-100">
              Kullanılabilir hazır net nakit
            </p>
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="bg-white dark:bg-slate-900 shadow-xl border border-slate-200/80 dark:border-slate-800 rounded-3xl overflow-hidden p-4 md:p-6">
        <DataTable
          ref={dt}
          value={payments}
          header={header}
          loading={loading}
          paginator
          rows={15}
          rowsPerPageOptions={[10, 15, 25, 50]}
          globalFilter={globalFilter}
          responsiveLayout="scroll"
          emptyMessage="Seçilen filtre kriterlerine uygun ödeme kaydı bulunamadı."
          className="p-datatable-sm custom-payments-table"
          rowClassName={(rowData) =>
            rowData.daysRemaining < 0
              ? "bg-rose-50/40 dark:bg-rose-950/10 hover:bg-rose-50/80 transition-colors"
              : rowData.daysRemaining === 0
              ? "bg-amber-50/40 dark:bg-amber-950/10 hover:bg-amber-50/80 transition-colors"
              : "hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors"
          }
        >
          {/* Due Date & Remaining Days */}
          <Column
            field="dueDate"
            header="VADE TARİHİ"
            sortable
            className="text-[11px] font-bold text-slate-500 w-40"
            body={(rowData) => {
              const d = new Date(rowData.dueDate);
              const formatted = Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString("tr-TR");
              return (
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1.5 font-bold text-slate-700 dark:text-slate-200 text-xs">
                    <i className="pi pi-calendar text-slate-400 text-xs" />
                    <span>{formatted}</span>
                  </div>
                  <div>{getDaysRemainingBadge(rowData)}</div>
                </div>
              );
            }}
          />

          {/* Payee / Target */}
          <Column
            field="payeeName"
            header="KİME / ALACAKLI"
            sortable
            className="text-sm"
            body={(rowData) => (
              <div
                className={`flex items-center ${rowData.actionUrl ? "cursor-pointer hover:text-blue-600 transition-colors" : ""}`}
                onClick={() => rowData.actionUrl && navigate(rowData.actionUrl)}
              >
                <div className="!h-7 !w-7 shrink-0 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center mr-2">
                  <i className="pi pi-building text-blue-500 text-[11px]"></i>
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="font-bold text-slate-700 dark:text-slate-200 text-xs">
                    {rowData.payeeName}
                  </span>
                  {rowData.branchName && (
                    <span className="text-[10px] text-slate-400 font-medium">
                      Şube: {rowData.branchName}
                    </span>
                  )}
                </div>
              </div>
            )}
          />

          {/* Source Type Badge */}
          <Column
            field="sourceType"
            header="İŞLEM TÜRÜ"
            sortable
            className="w-44"
            body={(rowData) => getSourceTypeBadge(rowData.sourceType)}
          />

          {/* Description & Document No */}
          <Column
            field="description"
            header="AÇIKLAMA / BELGE NO"
            className="text-sm"
            body={(rowData) => (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {rowData.documentNo && (
                    <span className="inline-block text-[10px] font-bold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 w-max">
                      {rowData.documentNo}
                    </span>
                  )}
                  {rowData.financialAccountName && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/60 px-1.5 py-0.5 rounded border border-blue-200 dark:border-blue-800/60 w-max">
                      <i className="pi pi-building text-[9px]" />
                      <span>{rowData.financialAccountName}</span>
                    </span>
                  )}
                </div>
                <span className="text-[11px] text-slate-500 font-medium max-w-[350px] truncate">
                  {rowData.description}
                </span>
              </div>
            )}
          />

          {/* Amount / Remaining */}
          <Column
            field="remainingAmount"
            header="KALAN TUTAR"
            sortable
            className="w-40"
            body={(rowData) => (
              <div className="flex flex-col">
                <span
                  className={`font-black ${
                    rowData.daysRemaining < 0
                      ? "text-red-600"
                      : rowData.daysRemaining === 0
                      ? "text-amber-600"
                      : "text-slate-700 dark:text-slate-200"
                  }`}
                >
                  {formatMoney(rowData.remainingAmount)}
                </span>
                {rowData.paidAmount > 0 && (
                  <span className="text-[10px] text-emerald-600 font-bold">
                    Ödenen: {formatMoney(rowData.paidAmount)}
                  </span>
                )}
              </div>
            )}
          />

          {/* Actions - Quick Pay Button */}
          <Column
            header="İŞLEM"
            className="w-32 text-center"
            body={(rowData) => (
              <div className="flex items-center justify-center">
                <Button
                  label="Ödeme Yap"
                  icon="pi pi-credit-card"
                  className="p-button-sm !bg-emerald-600 hover:!bg-emerald-700 !border-none !text-white !text-xs !font-black !py-1.5 !px-3 rounded-xl shadow-sm shadow-emerald-600/20 flex items-center gap-1.5"
                  onClick={() => openPaymentModal(rowData)}
                  tooltip="Bu kaleme hızlı ödeme yap"
                />
              </div>
            )}
          />
        </DataTable>
      </div>

      {/* REUSABLE QUICK PAYMENT DIALOG */}
      <QuickPaymentDialog
        visible={isPaymentDialogVisible}
        onHide={() => setIsPaymentDialogVisible(false)}
        payment={selectedPayment}
        onSuccess={({ payment: paidItem, amount }) => {
          toast.current?.show({
            severity: "success",
            summary: "Ödeme Başarılı",
            detail: `${paidItem.payeeName} için ${formatMoney(amount)} tutarında ödeme kaydedildi.`,
          });
          fetchPayments();
        }}
      />

      <style>{`
        .custom-payments-table .p-datatable-header {
          background: transparent;
          border: none;
          padding: 0 0 1rem 0;
        }
        .custom-payments-table .p-datatable-thead > tr > th {
          background: #f8fafc;
          font-size: 11px;
          font-weight: 800;
          color: #64748b;
          border-top: none;
          border-bottom: 1px solid #e2e8f0;
          padding: 0.85rem 1rem;
        }
        .dark .custom-payments-table .p-datatable-thead > tr > th {
          background: #0f172a;
          color: #94a3b8;
          border-bottom: 1px solid #1e293b;
        }
        .custom-payments-table .p-datatable-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9;
          padding: 0.85rem 1rem;
        }
        .dark .custom-payments-table .p-datatable-tbody > tr > td {
          border-bottom: 1px solid #1e293b;
        }
      `}</style>
    </div>
  );
}
